import { CurrencyDollar } from '@phosphor-icons/react'
import { formatCurrency } from '../../hooks/useExchangeRate'
import { totalsByCurrencyEntries, type TotalsByCurrency } from '../../utils/offerTotals'

interface Props {
  totalsByCurrency: TotalsByCurrency | null | undefined
  variant?: 'full' | 'compact' | 'inline'
  className?: string
  emptyLabel?: string
  align?: 'start' | 'end'
}

/**
 * Renders offer totals as separate cards/lines per currency.
 * No cross-currency conversion. Replaces CurrencyDisplay in offer flows.
 */
export function MultiCurrencyTotal({
  totalsByCurrency,
  variant = 'full',
  className = '',
  emptyLabel = '—',
  align = 'end',
}: Props) {
  const entries = totalsByCurrencyEntries(totalsByCurrency)

  if (entries.length === 0) {
    return <span className={`text-gray-400 ${className}`}>{emptyLabel}</span>
  }

  if (variant === 'inline') {
    return (
      <span className={`inline-flex flex-wrap gap-x-2 ${className}`}>
        {entries.map(([currency, value], i) => (
          <span key={currency} className="font-semibold text-gray-900 whitespace-nowrap">
            {formatCurrency(value, currency)}
            {i < entries.length - 1 && <span className="ml-2 text-gray-300">·</span>}
          </span>
        ))}
      </span>
    )
  }

  if (variant === 'compact') {
    return (
      <div className={`flex flex-col gap-0.5 ${align === 'end' ? 'items-end' : 'items-start'} ${className}`}>
        {entries.map(([currency, value]) => (
          <div key={currency} className="text-sm font-semibold text-gray-900 whitespace-nowrap tabular-nums">
            {formatCurrency(value, currency)}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className={`space-y-2 ${className}`}>
      {entries.map(([currency, value]) => (
        <div
          key={currency}
          className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg"
        >
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center">
              <CurrencyDollar size={16} weight="bold" className="text-white" />
            </div>
            <div>
              <p className="text-xs font-medium text-emerald-700 uppercase tracking-wide">
                {currency}
              </p>
            </div>
          </div>
          <p className="text-xl font-bold text-emerald-900 tabular-nums">
            {formatCurrency(value, currency)}
          </p>
        </div>
      ))}
    </div>
  )
}
